import {
  collection,
  addDoc,
  Timestamp,
  where,
  getDocs,
  doc,
  getDoc,
  query,
  updateDoc,
  limit, 
  or,
  and,
} from "firebase/firestore";

import { firestore } from "../firebase/firebaseConfig";

/**
 * Creates a new trade record in the trades collection.
 * @param {Object} tradeDetails - Details of the trade.
 * @returns {Promise<Object>} The created trade document with its ID.
 */
export const createTrade = async (tradeDetails) => {
  try {
    const tradesRef = collection(firestore, "trades");
    const tradeData = {
      ...tradeDetails,
      status: "pending",
      createdAt: Timestamp.now(),
    };
    const tradeDocRef = await addDoc(tradesRef, tradeData);

    return { id: tradeDocRef.id, ...tradeData };
  } catch (error) {
    console.error("[createTrade] Error creating trade:", error);
    throw new Error("Failed to create trade. Please try again.");
  }
};

/**
 * Fetches trades based on query parameters (e.g., buyerId, sellerId, status).
 * @param {Object} conditions - Query conditions (e.g., { buyerId: "123", status: "pending" }).
 * @returns {Promise<Array>} An array of trade documents.
 */
export const fetchTrades = async (conditions = {}) => {
  try {
    const tradesRef = collection(firestore, "trades");
    const conditionsArray = Object.entries(conditions).map(([key, value]) =>
      where(key, "==", value)
    );
    const tradesQuery = query(tradesRef, ...conditionsArray);

    const querySnapshot = await getDocs(tradesQuery);
    if (querySnapshot.empty) {
      console.warn("[fetchTrades] No trades found with the provided conditions.");
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchTrades] Error fetching trades:", error);
    throw new Error("Failed to fetch trades. Please try again.");
  }
};

/**
 * Updates the status of a trade (e.g., to "completed" or "disputed").
 * @param {string} tradeId - The ID of the trade to update.
 * @param {string} status - The new status of the trade.
 * @param {Object} [additionalFields] - Any additional fields to update (e.g., `completedAt`).
 * @returns {Promise<void>}
 */
export const updateTradeStatus = async (tradeId, status, additionalFields = {}) => {
  try {
    const tradeDocRef = doc(firestore, "trades", tradeId);
    await updateDoc(tradeDocRef, {
      status,
      ...additionalFields,
    });
    console.log(`[updateTradeStatus] Trade ${tradeId} updated to status: ${status}`);
  } catch (error) {
    console.error("[updateTradeStatus] Error updating trade status:", error);
    throw new Error("Failed to update trade status. Please try again.");
  }
};

/**
 * Fetches a trade by its ID.
 * @param {string} tradeId - The unique ID of the trade.
 * @returns {Promise<Object|null>} The trade document or null if not found.
 */
export const fetchTradeById = async (tradeId) => {
  try {
    const tradeDocRef = doc(firestore, "trades", tradeId);
    const tradeDoc = await getDoc(tradeDocRef);

    if (!tradeDoc.exists()) {
      console.warn(`[fetchTradeById] Trade not found for ID: ${tradeId}`);
      return null;
    }

    return { id: tradeDoc.id, ...tradeDoc.data() };
  } catch (error) {
    console.error("[fetchTradeById] Error fetching trade by ID:", error);
    throw new Error("Failed to fetch trade. Please try again.");
  }
};

// Updated fetchTradeByListingId
export const fetchTradeByListingId = async (listingId, userId) => {
  console.log("Fetching trade for listingId:", listingId, "and userId:", userId); // Log input values
  try {
      const tradesRef = collection(firestore, "trades");
      const tradeQuery = query(
          tradesRef,
          and( // Use 'and' to combine the filters
              where("listingId", "==", listingId),
              or(
                  where("sellerId", "==", userId),
                  where("buyerId", "==", userId)
              )
          ),
          limit(1)
      );

      console.log("Trade Query:", tradeQuery); // Log the query object

      const querySnapshot = await getDocs(tradeQuery);

      if (querySnapshot.empty) {
        console.log("No trade found matching the query."); // Log if no trade is found

          return null;
      }

      const tradeDoc = querySnapshot.docs[0];
      return { id: tradeDoc.id, ...tradeDoc.data() };
  } catch (error) {
      console.error("Error fetching trade by listingId:", error);
      throw error;
  }
};



